import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Alert,
  Snackbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { Add as AddIcon, Business as BusinessIcon, Edit as EditIcon } from '@mui/icons-material';
import MainLayout from '../../../../components/Layout/MainLayout';
import httpClient from '../../../../utils/httpClient';
import { TelegramStatusEnum } from '../../enums/telegram';

interface Customer {
  id: string;
  fantasyName: string;
  registrationId?: string;
  responsableName: string;
  responsableEmail: string;
  status: 'Ativo' | 'Inativo' | 'Pendente';
  createdAt: Date;
  telegramActive?: boolean;
  telegramStatus?: number; // 0=NONE, 1=PENDING_TO_CREATING_GROUP, 2=ACTIVED_GROUP
}

interface ApiCustomer {
  guid: string;
  fantasyName: string;
  responsableName: string;
  email: string;
  status: boolean;
  statusDescription: string;
  createdDate: string;
  createdDateDescription: string;
  telegramConfigurations?: {
    status?: number;
    active?: boolean;
    groupChatId?: string;
  } | null;
}

// Pagination state
const DEFAULT_PAGE_SIZE = 10;

const CustomerManagement: React.FC = () => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [formData, setFormData] = useState({
    fantasyName: '',
    registrationId: '',
    responsableName: '',
    responsableEmail: ''
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('error');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [editFormData, setEditFormData] = useState({
    fantasyName: '',
    registrationId: '',
    responsableName: '',
    responsableEmail: '',
    telegramEnabled: false,
    telegramGroupChatId: '',
    telegramStatus: '0' // 0 = Selecione (placeholder), NA = N/A, PENDING, CREATED
  });
  const [lockTelegramOnEnable, setLockTelegramOnEnable] = useState<boolean>(false);

  const mapApiToCustomer = (api: ApiCustomer): Customer => {
    const statusDesc = (api.statusDescription || '').trim();
    let status: Customer['status'] = 'Pendente';
    if (statusDesc.toLowerCase() === 'ativo') status = 'Ativo';
    else if (statusDesc.toLowerCase() === 'desativado' || statusDesc.toLowerCase() === 'inativo') status = 'Inativo';

    const created = api.createdDate ? new Date(api.createdDate) : new Date();
    const tcfg = api.telegramConfigurations || null;
    const tActive = !!tcfg?.active;
    const tStatusNum = (tcfg?.status === undefined || tcfg?.status === null) ? undefined : Number(tcfg.status);

    return {
      id: api.guid,
      fantasyName: api.fantasyName || '-',
      registrationId: undefined,
      responsableName: api.responsableName || '-',
      responsableEmail: api.email || '-',
      status,
      createdAt: created,
      telegramActive: tActive,
      telegramStatus: tStatusNum
    };
  };

  const loadCustomers = async () => {
    try {
      const res = await httpClient.get('/api/v1/portal/customer', {
        params: {
          pageNumber,
          pageSize
        }
      });

      const payload = res?.data;
      const ok = payload?.success === true && Number(payload?.status) === 200;
      const list: ApiCustomer[] = Array.isArray(payload?.data) ? payload.data : [];

      if (!ok) {
        const errors = Array.isArray(payload?.errors) ? payload.errors : [];
        const firstErrorMessage = errors[0]?.errorMessage || t('customer_mgmt_error_load_customers');
        setAlertMessage(firstErrorMessage);
        setAlertSeverity('error');
        setShowAlert(true);
        setCustomers([]);
        return;
      }

      const mapped = list.map(mapApiToCustomer);
      setCustomers(mapped);
    } catch (err: any) {
      const apiMsg = err?.response?.data?.errors?.[0]?.errorMessage;
      setAlertMessage(apiMsg || t('customer_mgmt_error_connection'));
      setAlertSeverity('error');
      setShowAlert(true);
      setCustomers([]);
    }
  };

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCNPJ = (value: string) => {
    // Remove tudo que não é dígito
    const cleanValue = value.replace(/\D/g, '');
    
    // Aplica a máscara do CNPJ
    if (cleanValue.length <= 14) {
      return cleanValue
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return value;
  };

  const handleCNPJChange = (value: string) => {
    const formattedValue = formatCNPJ(value);
    handleInputChange('registrationId', formattedValue);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    if (!formData.fantasyName || !formData.responsableName || !formData.responsableEmail) {
      setAlertMessage(t('customer_mgmt_error_required_fields'));
      setAlertSeverity('error');
      setShowAlert(true);
      return;
    }

    if (!validateEmail(formData.responsableEmail)) {
      setAlertMessage(t('common_error_invalid_email'));
      setAlertSeverity('error');
      setShowAlert(true);
      return;
    }

    // Verificar se email já existe
    const emailExists = customers.some(customer => customer.responsableEmail === formData.responsableEmail);
    if (emailExists) {
      setAlertMessage(t('customer_mgmt_error_email_exists'));
      setAlertSeverity('error');
      setShowAlert(true);
      return;
    }

    // Verificar se CNPJ já existe (se fornecido)
    if (formData.registrationId) {
      const cnpjExists = customers.some(customer => customer.registrationId === formData.registrationId);
      if (cnpjExists) {
        setAlertMessage(t('customer_mgmt_error_cnpj_exists'));
        setAlertSeverity('error');
        setShowAlert(true);
        return;
      }
    }
    try {
      const payload = {
        fantasyName: formData.fantasyName.trim(),
        registrationId: (formData.registrationId || '').replace(/\D/g, ''),
        responsableName: formData.responsableName.trim(),
        responsableEmail: formData.responsableEmail.trim(),
      };

      const res = await httpClient.post('/api/v1/portal/enrollment/pre-register', payload);
      const response = res?.data;

      // Determine success based on presence of data or explicit flags
      const data = response?.data;
      const errors = Array.isArray(response?.errors) ? response.errors : [];
      const hasData = data && typeof data === 'object';

      if (!hasData || (response?.success !== true)) {
        const message = (errors[0]?.errorMessage || errors[0] || t('customer_mgmt_error_pre_register_failed'));
        setAlertMessage(message);
        setAlertSeverity('error');
        setShowAlert(true);
        return;
      }

      const apiGuid: string = data.guid || Date.now().toString();
      const apiFantasyName: string = data.fantasyName || payload.fantasyName;
      const apiRegistrationId: string = data.registrationId || payload.registrationId;
      const apiCreatedDate: string | undefined = data.createdDate;

      const newCustomer: Customer = {
        id: apiGuid,
        fantasyName: apiFantasyName,
        registrationId: apiRegistrationId ? formatCNPJ(apiRegistrationId) : undefined,
        responsableName: payload.responsableName,
        responsableEmail: payload.responsableEmail,
        status: 'Pendente',
        createdAt: apiCreatedDate ? new Date(apiCreatedDate) : new Date(),
      };

      setCustomers(prev => [...prev, newCustomer]);

      // Limpar formulário
      setFormData({
        fantasyName: '',
        registrationId: '',
        responsableName: '',
        responsableEmail: ''
      });

      setAlertMessage(t('customer_mgmt_success_pre_registered'));
      setAlertSeverity('success');
      setShowAlert(true);
    } catch (err: any) {
      const resp = err?.response?.data;
      const collectedErrors: string[] = [];
      if (Array.isArray(resp?.errors)) {
        for (const it of resp.errors) {
          const m = (it && (it.errorMessage || it.message)) as string | undefined;
          if (m) collectedErrors.push(m);
        }
      }
      const message = collectedErrors[0] || t('customer_mgmt_error_pre_register_try_again');
      setAlertMessage(message);
      setAlertSeverity('error');
      setShowAlert(true);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativo':
        return 'success';
      case 'Pendente':
        return 'warning';
      case 'Inativo':
        return 'error';
      default:
        return 'default';
    }
  };
  const mapTelegramStatusForTable = (code?: number): string => {
    const n = typeof code === 'number' ? code : TelegramStatusEnum.NONE;
    switch (n) {
      case TelegramStatusEnum.PENDING_TO_CREATING_GROUP:
        return t('customer_mgmt_telegram_table_pending');
      case TelegramStatusEnum.ACTIVED_GROUP:
        return t('customer_mgmt_telegram_table_active');
      case TelegramStatusEnum.NONE:
      default:
        return t('customer_mgmt_telegram_table_none');
    }
  };

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    const initialEnabled = !!customer.telegramActive;
    const initialStatusNum = (customer.telegramStatus === undefined || customer.telegramStatus === null) ? 0 : Number(customer.telegramStatus);
    // Map numeric to UI string
    const toStatusString = (num: number): '0' | 'PENDING_TO_CREATING_GROUP' | 'ACTIVED_GROUP' => {
      if (num === TelegramStatusEnum.ACTIVED_GROUP) return 'ACTIVED_GROUP';
      if (num === TelegramStatusEnum.PENDING_TO_CREATING_GROUP) return 'PENDING_TO_CREATING_GROUP';
      return '0';
    };
    // Lock rule base: null or status 0
    const shouldLock = (customer.telegramStatus === undefined || initialStatusNum === 0);
    setLockTelegramOnEnable(shouldLock);
    setEditFormData({
      fantasyName: customer.fantasyName,
      registrationId: customer.registrationId || '',
      responsableName: customer.responsableName,
      responsableEmail: customer.responsableEmail,
      telegramEnabled: initialEnabled,
      telegramGroupChatId: '',
      telegramStatus: shouldLock && initialEnabled ? 'PENDING_TO_CREATING_GROUP' : toStatusString(initialStatusNum)
    });
    setEditModalOpen(true);
  };

  const handleEditInputChange = (field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditSubmit = async () => {
    if (!selectedCustomer) return;

    // Validação de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editFormData.responsableEmail)) {
      setAlertMessage(t('common_error_invalid_email'));
      setAlertSeverity('error');
      setShowAlert(true);
      return;
    }

    // Verificar se CNPJ já existe (exceto o próprio cliente)
    if (editFormData.registrationId) {
      const cnpjExists = customers.some(customer => 
        customer.registrationId === editFormData.registrationId && customer.id !== selectedCustomer.id
      );
      if (cnpjExists) {
        setAlertMessage(t('customer_mgmt_error_cnpj_exists'));
        setAlertSeverity('error');
        setShowAlert(true);
        return;
      }
    }

    try {
      // Align to backend enum TelegramStatusEnum: NONE=0, PENDING_TO_CREATING_GROUP=1, ACTIVED_GROUP=2
      const statusMap: Record<string, number> = {
        '0': TelegramStatusEnum.NONE,
        'PENDING_TO_CREATING_GROUP': TelegramStatusEnum.PENDING_TO_CREATING_GROUP,
        'ACTIVED_GROUP': TelegramStatusEnum.ACTIVED_GROUP
      };
      const telegramEnabled = !!editFormData.telegramEnabled;
      const telegramStatusNumeric = telegramEnabled ? (statusMap[String(editFormData.telegramStatus)] ?? 0) : 0;

      const payload = {
        fantasyName: editFormData.fantasyName,
        guid: selectedCustomer.id,
        registrationId: (editFormData.registrationId || '').replace(/\D/g, ''),
        responsableName: editFormData.responsableName,
        responsableEmail: editFormData.responsableEmail,
        telegramEnabled,
        telegramGroupChatId: telegramEnabled ? (editFormData.telegramGroupChatId || '').trim() : '',
        telegramStatus: telegramStatusNumeric
      };

      const res = await httpClient.put('/api/v1/portal/customer/update-basic-info', payload);

      // Trata respostas com errors dentro de 200 OK
      const maybeErrors = (res?.data && (res.data as any).errors) || [];
      if (Array.isArray(maybeErrors) && maybeErrors.length > 0) {
        const msg = maybeErrors.map((e: any) => `${e.propertyName || ''}: ${e.errorMessage || ''}`.trim()).join(' | ');
        setAlertMessage(msg || t('customer_mgmt_error_update_failed'));
        setAlertSeverity('error');
        setShowAlert(true);
        return;
      }

      // Atualiza estado local com os novos dados
      setCustomers(prev => prev.map(customer => 
        customer.id === selectedCustomer.id 
          ? {
              ...customer,
              fantasyName: editFormData.fantasyName,
              registrationId: editFormData.registrationId || undefined,
              responsableName: editFormData.responsableName,
              responsableEmail: editFormData.responsableEmail
            }
          : customer
      ));

      setEditModalOpen(false);
      setSelectedCustomer(null);
      setAlertMessage(t('customer_mgmt_success_customer_updated'));
      setAlertSeverity('success');
      setShowAlert(true);
    } catch (error: any) {
      const apiErrors = error?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        const msg = apiErrors.map((e: any) => `${e.propertyName || ''}: ${e.errorMessage || ''}`.trim()).join(' | ');
        setAlertMessage(msg || t('customer_mgmt_error_update_failed'));
      } else {
        setAlertMessage(t('customer_mgmt_error_update_try_again'));
      }
      setAlertSeverity('error');
      setShowAlert(true);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedCustomer(null);
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon />
          {t('customer_mgmt_title')}
        </Typography>

        {/* Formulário de Criação */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('customer_mgmt_create_title')}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label={t('customer_mgmt_label_fantasy_name')}
                value={formData.fantasyName}
                onChange={(e) => handleInputChange('fantasyName', e.target.value)}
                variant="outlined"
                helperText={t('customer_mgmt_helper_fantasy_name')}
                inputProps={{ maxLength: 80 }}
              />
            </Grid>
            
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('customer_mgmt_label_cnpj')}
                value={formData.registrationId}
                onChange={(e) => handleCNPJChange(e.target.value)}
                variant="outlined"
                placeholder={t('customer_mgmt_placeholder_cnpj')}
                helperText={t('customer_mgmt_helper_cnpj_optional')}
                inputProps={{ maxLength: 19 }}
              />
            </Grid>
            
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('customer_mgmt_label_responsible_name')}
                value={formData.responsableName}
                onChange={(e) => handleInputChange('responsableName', e.target.value)}
                variant="outlined"
                helperText={t('customer_mgmt_helper_responsible_name')}
                inputProps={{ maxLength: 80 }}
              />
            </Grid>
            
            <Grid component={'div' as React.ElementType} item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('customer_mgmt_label_responsible_email')}
                type="email"
                value={formData.responsableEmail}
                onChange={(e) => handleInputChange('responsableEmail', e.target.value)}
                variant="outlined"
                helperText={t('customer_mgmt_helper_responsible_email')}
                inputProps={{ maxLength: 80 }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleSubmit}
              sx={{ mr: 2 }}
            >
              {t('customer_mgmt_btn_create_customer')}
            </Button>
          </Box>
        </Paper>

        {/* Lista de Clientes */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('customer_mgmt_list_title')}
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('customer_mgmt_table_fantasy_name')}</TableCell>
                  <TableCell>{t('customer_mgmt_table_cnpj')}</TableCell>
                  <TableCell>{t('customer_mgmt_table_responsible_name')}</TableCell>
                  <TableCell>{t('customer_mgmt_table_email')}</TableCell>
                  <TableCell>{t('customer_mgmt_table_status')}</TableCell>
                  <TableCell>{t('customer_mgmt_table_telegram_group')}</TableCell>
                  <TableCell>{t('customer_mgmt_table_created_at')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => handleEditCustomer(customer)}
                        sx={{ 
                          fontWeight: 'bold',
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText'
                          }
                        }}
                      >
                        {customer.fantasyName}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {customer.registrationId || '-'}
                    </TableCell>
                    <TableCell>{customer.responsableName}</TableCell>
                    <TableCell>{customer.responsableEmail}</TableCell>
                    <TableCell>
                      <Chip
                        label={customer.status === 'Ativo' ? t('customer_mgmt_status_active') : customer.status === 'Inativo' ? t('customer_mgmt_status_inactive') : t('customer_mgmt_status_pending')}
                        color={getStatusColor(customer.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {mapTelegramStatusForTable(customer.telegramStatus)}
                    </TableCell>
                    <TableCell>
                      {customer.createdAt.toLocaleDateString('pt-BR')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Alert/Snackbar */}
        <Snackbar
          open={showAlert}
          autoHideDuration={6000}
          onClose={() => setShowAlert(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setShowAlert(false)}
            severity={alertSeverity}
            sx={{ width: '100%' }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>

        {/* Edit Customer Modal */}
        <Dialog open={editModalOpen} onClose={handleCloseEditModal} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon />
              {t('customer_mgmt_dialog_edit_title')}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('customer_mgmt_label_fantasy_name')}
                  value={editFormData.fantasyName}
                  onChange={(e) => handleEditInputChange('fantasyName', e.target.value)}
                  inputProps={{ maxLength: 80 }}
                />
              </Grid>
              
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('customer_mgmt_label_cnpj')}
                  value={editFormData.registrationId}
                  onChange={(e) => handleEditInputChange('registrationId', formatCNPJ(e.target.value))}
                  placeholder={t('customer_mgmt_placeholder_cnpj')}
                  inputProps={{ maxLength: 19 }}
                />
              </Grid>
              
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('customer_mgmt_label_responsible_name')}
                  value={editFormData.responsableName}
                  onChange={(e) => handleEditInputChange('responsableName', e.target.value)}
                  inputProps={{ maxLength: 80 }}
                />
              </Grid>
              
              <Grid component={'div' as React.ElementType} item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('customer_mgmt_label_responsible_email')}
                  type="email"
                  value={editFormData.responsableEmail}
                  onChange={(e) => handleEditInputChange('responsableEmail', e.target.value)}
                  inputProps={{ maxLength: 80 }}
                />
              </Grid>

              {/* Section Separator for Telegram */}
              <Grid component={'div' as React.ElementType} item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              {/* Telegram Section */}
              <Grid component={'div' as React.ElementType} item xs={12}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  {t('customer_mgmt_telegram_section_title')}
                </Typography>
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!editFormData.telegramEnabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setEditFormData(prev => ({
                          ...prev,
                          telegramEnabled: checked,
                          // Apply lock rule: when enabling and lock is true, enforce pending and empty group ID
                          telegramStatus: lockTelegramOnEnable
                            ? (checked ? 'PENDING_TO_CREATING_GROUP' : '0')
                            : prev.telegramStatus,
                          telegramGroupChatId: lockTelegramOnEnable
                            ? ''
                            : prev.telegramGroupChatId
                        }));
                      }}
                      color="primary"
                    />
                  }
                  label={t('customer_mgmt_telegram_enabled_label')}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t('customer_mgmt_telegram_group_id_label')}
                  value={editFormData.telegramGroupChatId}
                  onChange={(e) => handleEditInputChange('telegramGroupChatId', e.target.value)}
                  size="small"
                  disabled={!editFormData.telegramEnabled || (lockTelegramOnEnable && editFormData.telegramEnabled)}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>
              <Grid component={'div' as React.ElementType} item xs={12} md={4}>
                <FormControl fullWidth size="small" disabled={!editFormData.telegramEnabled || (lockTelegramOnEnable && editFormData.telegramEnabled)}>
                  <InputLabel>{t('customer_mgmt_telegram_status_label')}</InputLabel>
                   <Select
                     value={editFormData.telegramStatus}
                     label={t('customer_mgmt_telegram_status_label')}
                     onChange={(e) => handleEditInputChange('telegramStatus', String(e.target.value))}
                     displayEmpty
                     renderValue={(selected) => {
                       const val = String(selected ?? '0');
                       const isPlaceholder = val === '0';
                       const map: Record<string, string> = {
                         '0': t('customer_mgmt_telegram_status_select'),
                         'PENDING_TO_CREATING_GROUP': t('customer_mgmt_telegram_status_pending'),
                         'ACTIVED_GROUP': t('customer_mgmt_telegram_status_created')
                       };
                       const label = map[val] || '';
                       return (
                         <span style={{ fontStyle: isPlaceholder ? 'italic' : 'normal', color: isPlaceholder ? 'rgba(0,0,0,0.6)' : undefined }}>
                           {label}
                         </span>
                       );
                     }}
                   >
                     <MenuItem value={'0'} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                       {t('customer_mgmt_telegram_status_select')}
                     </MenuItem>
                     <MenuItem value={'PENDING_TO_CREATING_GROUP'}>{t('customer_mgmt_telegram_status_pending')}</MenuItem>
                     <MenuItem value={'ACTIVED_GROUP'}>{t('customer_mgmt_telegram_status_created')}</MenuItem>
                   </Select>
                 </FormControl>
               </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditModal}>
              {t('customer_mgmt_btn_cancel')}
            </Button>
            <Button 
              onClick={handleEditSubmit} 
              variant="contained"
              disabled={!editFormData.fantasyName || !editFormData.responsableName || !editFormData.responsableEmail}
            >
              {t('customer_mgmt_btn_save_changes')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default CustomerManagement;
